import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router } from "@angular/router";
import { CurrentCaseService } from "@modules/common/services/current-case.service";
import { RouteModelService } from '@modules/common/services/route-model.service';
import { ZendeskSSOQueryParamsService } from "@modules/login-zendesk-sso/services/zendesk-sso-query-params.service";

@Injectable({
    providedIn: 'root'
})
export class RedirectToLoginService {
    constructor(
        private readonly _router: Router,
        private readonly _currentCaseService: CurrentCaseService,
        private readonly _routeModelService: RouteModelService,
        private readonly _zendeskSSOQueryParamsService: ZendeskSSOQueryParamsService
    ) { }

    private _redirectedFromUrl: any[] | undefined;
    private _queryParams: { [key: string]: string } | undefined;

    public redirectToLogin(redirectedFromRoute?: ActivatedRouteSnapshot): void {
        this.clearRedirectedFromUrl();

        const activatedRouteSnapshot = redirectedFromRoute ?? this._router.routerState.snapshot.root;
        const routeModel = this._routeModelService.getRouteModel(activatedRouteSnapshot);
        const fromUrl = routeModel.urlWithPopulatedParameters;

        const zendeskSSOQueryParams = this._zendeskSSOQueryParamsService.getZendeskSSOQueryParams(activatedRouteSnapshot);

        //We only want to do the redirect if the page we're coming from isn't a login page, unless
        //that page is the AccountSettings page
        if (fromUrl && (fromUrl.indexOf('Login') === -1 || fromUrl.indexOf('AccountSettings') !== -1)) {
            this._redirectedFromUrl = fromUrl.slice();
            //Add an extra element to the URL containing Angular's custom query params (the ; separated parts)
            for (let k in routeModel.params) {
                if (routeModel.urlWithParameterPlaceholders.indexOf(':' + k) == -1) {
                    if (this._redirectedFromUrl.length == routeModel.urlWithParameterPlaceholders.length) {
                        this._redirectedFromUrl.push({});
                    }

                    this._redirectedFromUrl[this._redirectedFromUrl.length - 1][k] = routeModel.params[k];
                }
            }
            this._queryParams = Object.assign({}, routeModel.queryParams);

            //Query params for Zendesk SSO are only used for the Zendesk SSO login page. Remove them
            //from the redirect.
            if (zendeskSSOQueryParams) {
                for (let queryParamName in zendeskSSOQueryParams) {
                    delete this._queryParams[queryParamName];
                }
            }
        }

        //We only want to use the case branding for the login page
        //if the user isn't in the main Stingray site. If the user
        //is in Stingray, then we have to use Winston branding.
        const caseName = this._currentCaseService.getCurrentCaseName();
        const includeCaseName = fromUrl && fromUrl.indexOf('Login') !== -1 && !!caseName;
        const loginURL = (includeCaseName ? '/' + caseName : '')
            + '/Login'
            + (zendeskSSOQueryParams ? '/ZendeskSSO' : '');
        const loginQueryParams = zendeskSSOQueryParams ? zendeskSSOQueryParams : null;

        this._router.navigate([loginURL], { queryParams: loginQueryParams });
    }

    public hasRedirectedFromUrl(): boolean {
        return !!this._redirectedFromUrl;
    }

    public navigateToRedirectedFromUrl(clearRedirectedFromUrl: boolean = true): void {
        this._router.navigate(this._redirectedFromUrl!, { queryParams: this._queryParams });

        if (clearRedirectedFromUrl) {
            this.clearRedirectedFromUrl();
        }
    }

    public clearRedirectedFromUrl(): void {
        this._redirectedFromUrl = undefined;
        this._queryParams = undefined;
    }
}